import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ApiError, setOfflineQueue } from "@/api/client";
import * as sync from "@/offline/sync";
import { registerServiceWorker } from "@/offline/registerServiceWorker";
import { AuthProvider } from "@/auth/AuthProvider";
import { ToastProvider } from "@/components/Toast";
import { App } from "./App";
import "./styles/index.css";

// The client queues without importing the outbox: it stays a transport, and the
// offline policy stays in one module (ADR 0009).
setOfflineQueue(async (request) => sync.enqueue({ ...request, method: "POST" }));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The client already retries a GET on a network failure or a 5xx; letting
      // React Query retry on top of that would multiply the wait on a bad
      // connection, which is exactly where a technician is least patient.
      retry: false,
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (_count, error) =>
        // Never blindly retry a mutation: a replayed movement without an
        // idempotency key would post twice.
        error instanceof ApiError && error.status === 0 ? false : false,
      // TanStack Query PAUSES a mutation when navigator.onLine is false, and
      // resumes it from memory when the connection returns. That is not durable:
      // close the tab, or reload, and the operation is gone. The whole point of
      // the outbox is surviving that (ADR 0009), so the mutation must be allowed
      // to run, fail, and be persisted by the client instead of being held here.
      networkMode: "always",
    },
  },
});

// Anything that lands from the queue changes stock, so everything is stale.
sync.setInvalidator(() => {
  void queryClient.invalidateQueries();
});
sync.start();
registerServiceWorker();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
